import CodeEditor from "components/CodeEditor";
import PolicyTypeBadge from "components/PolicyTypeBadge";
import { External } from "components/icons/generated";
import Box from "ds/components/Box";
import LinkButtonIcon from "ds/components/ButtonIcon/LinkButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tag from "ds/components/Tag";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import useDetachPolicy from "shared/Policy/useDetachPolicy";
import { StackPolicyAttachment } from "types/generated";
import { getAttachedLabel } from "utils/labels";

import { StackContext } from "../Context";
import { showPoliciesDetachConfirmationModal } from "./DetachConfirmationModal";
import { GET_POLICIES_REFETCH_QUERIES } from "./constants";
import styles from "./styles.module.css";

type StackPoliciesListItemProps = {
  policy: StackPolicyAttachment;
  stackLabels?: string[];
};

const StackPoliciesListItem = ({ policy, stackLabels }: StackPoliciesListItemProps) => {
  const isAutoAttached = policy.isAutoattached;
  const policyLink = `/policy/${policy.policyId}/`;
  const [isCollapsed, toggle] = useToggle(true);

  const { canManageStack } = useTypedContext(StackContext);

  const { detachPolicy, loading } = useDetachPolicy(GET_POLICIES_REFETCH_QUERIES);

  const onDelete = () => {
    detachPolicy(policy.id);
  };

  const handleDelete = () => {
    showPoliciesDetachConfirmationModal({
      policyName: policy.policyName,
      onConfirm: onDelete,
    });
  };

  return (
    <CardCollapsible
      ariaLevel={4}
      ariaLabel={policy.policyName}
      title={policy.policyName}
      size="small"
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={
        <LinkButtonIcon icon={External} to={policyLink} variant="ghost">
          Go to policy editor
        </LinkButtonIcon>
      }
      headerSuffix={
        <Box align="center" gap="medium" shrink="0">
          {/* FYI: show only the first auto attached label from the list */}
          {isAutoAttached && stackLabels && (
            <Tag tag={getAttachedLabel(stackLabels, policy.policyLabels)} withAutoAttach />
          )}

          <PolicyTypeBadge type={policy.policyType} />

          <DropdownMenuEllipsis tooltip="Policy actions" buttonVariant="ghost">
            <DropdownMenuItem href={policyLink}>Go to policy editor</DropdownMenuItem>

            {!isAutoAttached && (canManageStack.result || !!canManageStack.message) && (
              <DropdownMenuItem
                loading={loading}
                danger
                onAction={handleDelete}
                isDisabled={!canManageStack.result}
                tooltip={canManageStack.message}
              >
                Detach policy
              </DropdownMenuItem>
            )}
          </DropdownMenuEllipsis>
        </Box>
      }
    >
      <CodeEditor
        className={styles.codeEditor}
        body={policy.policyBody}
        language="rego"
        autoHeight
        maxAutoHeight={460}
        readOnly
      />
    </CardCollapsible>
  );
};

export default StackPoliciesListItem;
